import { Directive, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReferenceDataContextualHelps } from '../../../lib/constants/reference-data-contextual-helps';
import { ReferenceDataLabels } from '../../../lib/constants/reference-data-labels';
import { ReferenceDataOptionLists } from '../../../lib/constants/reference-data-option-lists';
import { ReferenceDataConstants } from './../../../lib/constants/reference-data-constants';
import { MortgageApplication } from './../models/mortgage-application.model';
import { MortgageApplicationStore } from './../services/mortgage-application-store';

@Directive()
export abstract class BaseMortgageApplicationComponent implements OnDestroy, OnInit {
  public mortgageApplication: MortgageApplication;
  public referenceDataLabels = ReferenceDataLabels;
  public referenceDataContextualHelps = ReferenceDataContextualHelps;
  public referenceDataOptionLists = ReferenceDataOptionLists;
  public sections: any;
  public hasSubmitValidationError?: boolean;
  public saveContinueButtonLabel = ReferenceDataLabels.SaveContinueId;
  public saveContinueToNextStepButtonLabel = ReferenceDataLabels.SaveContinueToNextStepId;

  protected destroySubject$ = new Subject();

  constructor(protected mortgageApplicationStore: MortgageApplicationStore) {}

  public ngOnInit(): void {
    this.mortgageApplicationStore.dataObservable.pipe(takeUntil(this.destroySubject$)).subscribe((data) => {
      this.mortgageApplication = data.mortgageApplicationEntity;
      this.initialize();
      this.sections = data.sections;
      this.hasSubmitValidationError =
        data.hasSubmitValidationError !== undefined ? data.hasSubmitValidationError : this.hasSubmitValidationError;
      if (this.mortgageApplication.isUnregisteredFactFind) {
        this.saveContinueButtonLabel = this.referenceDataLabels.ContinueToNextStep;
        this.saveContinueToNextStepButtonLabel = this.referenceDataLabels.ContinueToNextStep;
      }
    });
  }

  protected initialize(): void {}

  public isLoanGroupTypeFirstStart(): boolean {
    return this.mortgageApplicationStore.isLoanTypeInLoanGroup(
      this.mortgageApplication.getLoanType(),
      ReferenceDataConstants.FirstStartLoanGroupType
    );
  }

  public isLoanGroupTypeHelpToBuy(): boolean {
    return this.mortgageApplicationStore.isLoanTypeInLoanGroup(
      this.mortgageApplication.getLoanType(),
      ReferenceDataConstants.HelpToBuyLoanGroupType
    );
  }

  public isNICHALoanGroupType(): boolean {
    return this.mortgageApplicationStore.isLoanTypeInLoanGroup(
      this.mortgageApplication.getLoanType(),
      ReferenceDataConstants.NICHALoanGroupType
    );
  }

  public isLoanGroupTypeFamilyLink(): boolean {
    return this.mortgageApplicationStore.isLoanTypeInLoanGroup(
      this.mortgageApplication.getLoanType(),
      ReferenceDataConstants.FamilyLinkLoanGroupType
    );
  }

  public isLoanGroupTypeRetirementLink(): boolean {
    return this.mortgageApplicationStore.isLoanTypeInLoanGroup(
      this.mortgageApplication.getLoanType(),
      ReferenceDataConstants.RetirementLinkLoanGroupType
    );
  }

  public isLoanGroupTypeBtlAffordability(): boolean {
    return this.mortgageApplicationStore.isLoanTypeInLoanGroup(
      this.mortgageApplication.getLoanType(),
      ReferenceDataConstants.BuyToLetAffordabilityLoanGroupType
    );
  }

  public isResidentialLoanGroupTypeFirstStart(): boolean {
    return this.mortgageApplication && this.mortgageApplication.isResidential && this.isLoanGroupTypeFirstStart();
  }

  public isProductForNICHA(): boolean {
    if (this.mortgageApplication) {
      if (this.mortgageApplication.IsFullApp) {
        return this.isNICHALoanGroupType();
      } else if (this.mortgageApplication.IsFactFind) {
        return this.mortgageApplication.IsNICHAApplication === ReferenceDataOptionLists.AnswerTypeYes;
      }
    }
  }

  public checkIsValuationReportTypeCodeVisible(): boolean {
    if (this.mortgageApplication && this.mortgageApplication.IsFactFind) {
      return true;
    }

    if (!this.mortgageApplication && !this.mortgageApplication.Fee) {
      return false;
    }

    if (
      this.checkIsReportCompletedBeforeMortgageVisible() &&
      this.mortgageApplication.Fee &&
      this.mortgageApplication.Fee.IsValuationCompletedBeforeCode !== ReferenceDataOptionLists.AnswerTypeNo
    ) {
      this.mortgageApplication.Fee.ValuationReportTypeCode = undefined;

      return false;
    }

    return true;
  }

  public checkIsReportCompletedBeforeMortgageVisible(): boolean {
    return (
      this.mortgageApplication &&
      this.mortgageApplication.isScottishProperty &&
      this.mortgageApplication.isPurchase &&
      this.mortgageApplication.isResidential
    );
  }

  public checkIsValuationReportTypeDisabled(): boolean {
    return (
      this.mortgageApplication &&
      (this.mortgageApplication.IsApplicationReadOnly || !this.mortgageApplication.isPurchase)
    );
  }

  public ngOnDestroy(): void {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }
}
